import { Command } from "commander";
import { existsSync, readFileSync } from "node:fs";
import { resolve, basename } from "node:path";
import { spawn } from "node:child_process";
import chalk from "chalk";
import { select, confirm, input, checkbox } from "@inquirer/prompts";
import { readProjectConfig, updateProjectConfig } from "../project-config.js";
import type { BuildMode } from "../project-config.js";
import { readWorkflowState, updateWorkflowState } from "../workflow-state.js";
import type { WorkflowPhase } from "../workflow-state.js";
import { scaffoldProject } from "./setup.js";
import { runIntakeFlow } from "./intake.js";
import { generateBuildPrompt } from "../build-prompt.js";
import { fatalError, warnAction } from "../ux/errors.js";
import { createRouter } from "@fathom/router";
import type { TaskContext as RouterTaskContext } from "@fathom/router";

const PHASES: { id: WorkflowPhase; label: string; description: string }[] = [
  { id: "intake", label: "INTAKE", description: "Extract and estimate work items" },
  { id: "build", label: "BUILD", description: "Launch Claude Code with full context" },
  { id: "review", label: "REVIEW", description: "Track, reconcile, and calibrate" },
];

/**
 * Detect project state:
 * - "new": no .fathom/ directory
 * - "returning": has workflow state with incomplete features
 * - "initialized": has config/intent but no workflow state
 */
function detectProjectState(): "new" | "returning" | "initialized" {
  const cwd = process.cwd();
  const fathomDir = resolve(cwd, ".fathom");

  if (existsSync(fathomDir)) {
    const state = readWorkflowState();
    if (state && state.featuresTotal > 0 && state.featuresComplete < state.featuresTotal) {
      return "returning";
    }
    const config = readProjectConfig();
    if (config) return "initialized";
  }

  return "new";
}

/**
 * Run interactive intent capture flow.
 * Returns the captured intent config.
 */
async function captureIntentFlow(defaultProjectName: string): Promise<{
  project: string;
  description: string;
  audience?: string;
  values: Array<{ name: string; description: string; priority: "critical" | "high" | "medium" | "low"; details?: string }>;
  budget?: { monthly_limit: number; alert_threshold: number; prefer_batch: boolean; currency: string };
  guardrails?: { security?: { templates: string[]; custom: string[] }; quality: string[]; process: string[] };
  tech?: { stack: string[]; opinions: string[] };
}> {
  console.log(chalk.bold("  Let's capture your project's intent.\n"));

  const project = await input({
    message: "Project name:",
    default: defaultProjectName,
  });

  const description = await input({
    message: "What are you building? (describe in a sentence or two)",
  });

  const audience = await input({
    message: "Who is this for? (optional, press enter to skip)",
  });

  // Values — no limit
  const VALUE_OPTIONS = [
    { name: "Simplicity over features", value: "simplicity" },
    { name: "Works offline", value: "offline" },
    { name: "Accessibility (WCAG)", value: "accessibility" },
    { name: "Low AI spend", value: "low-cost" },
    { name: "Data privacy / security", value: "security" },
    { name: "Performance at scale", value: "performance" },
    { name: "Real-time collaboration", value: "realtime" },
    { name: "Internationalization", value: "i18n" },
    { name: "SEO / discoverability", value: "seo" },
    { name: "Rapid iteration speed", value: "speed" },
    { name: "API-first design", value: "api-first" },
    { name: "Test coverage", value: "testing" },
  ];

  const selectedValues = await checkbox({
    message: "What matters for this project? (select all that apply, space to toggle)",
    choices: VALUE_OPTIONS,
  });

  // Depth: optionally drill into each selected value for priority + details
  const values: Array<{ name: string; description: string; priority: "critical" | "high" | "medium" | "low"; details?: string }> = [];

  let skipRemaining = false;
  if (selectedValues.length > 0) {
    const drillDown = await confirm({
      message: `Set priorities for each? (${selectedValues.length} selected — or skip to default all to medium)`,
      default: false,
    });

    if (drillDown) {
      for (const val of selectedValues) {
        if (skipRemaining) {
          const label = VALUE_OPTIONS.find((o) => o.value === val)?.name ?? val;
          values.push({ name: val, description: label, priority: "medium" });
          continue;
        }
        const label = VALUE_OPTIONS.find((o) => o.value === val)?.name ?? val;
        const details = await input({
          message: `"${label}" — any specifics? (enter to skip, "done" to skip remaining)`,
        });
        if (details.toLowerCase() === "done") {
          values.push({ name: val, description: label, priority: "medium" });
          skipRemaining = true;
          continue;
        }
        const priority = await select({
          message: `"${label}" — priority?`,
          choices: [
            { name: "Critical", value: "critical" as const },
            { name: "High", value: "high" as const },
            { name: "Medium", value: "medium" as const },
            { name: "Low", value: "low" as const },
            { name: "Skip remaining (default medium)", value: "skip" as const },
          ],
          default: "medium",
        });
        if (priority === "skip") {
          values.push({ name: val, description: label, priority: "medium", ...(details ? { details } : {}) });
          skipRemaining = true;
          continue;
        }
        values.push({
          name: val,
          description: label,
          priority: priority as "critical" | "high" | "medium" | "low",
          ...(details ? { details } : {}),
        });
      }
    } else {
      // Default all to medium
      for (const val of selectedValues) {
        const label = VALUE_OPTIONS.find((o) => o.value === val)?.name ?? val;
        values.push({ name: val, description: label, priority: "medium" });
      }
    }
  }

  // Custom values
  if (!skipRemaining) {
    let addMore = selectedValues.length === 0;
    if (!addMore && selectedValues.length > 0) {
      addMore = await confirm({ message: "Add any custom values not in the list?", default: false });
    }
    while (addMore) {
      const customName = await input({ message: "Value name (or press enter to finish):" });
      if (!customName) break;
      const customDesc = await input({ message: `Description for "${customName}":` });
      values.push({ name: customName, description: customDesc, priority: "medium" });
      addMore = await confirm({ message: "Add another?", default: false });
    }
  }

  // Budget
  const wantBudget = await confirm({ message: "Set a monthly AI budget? (helps with model routing)", default: false });
  let budget: { monthly_limit: number; alert_threshold: number; prefer_batch: boolean; currency: string } | undefined;
  if (wantBudget) {
    const limitStr = await input({ message: "Monthly limit (USD):", default: "200" });
    budget = {
      monthly_limit: parseFloat(limitStr) || 200,
      alert_threshold: 0.8,
      prefer_batch: false,
      currency: "USD",
    };
  }

  // Guardrails
  const wantGuardrails = await confirm({ message: "Add guardrails (security/quality rules)?", default: true });
  let guardrails: { security?: { templates: string[]; custom: string[] }; quality: string[]; process: string[] } | undefined;
  if (wantGuardrails) {
    const templates = await checkbox({
      message: "Guardrail templates (select all that apply):",
      choices: [
        { name: "OWASP Top 10 — web security (injection, XSS, auth, CSRF, etc.)", value: "owasp-top-10" },
        { name: "WCAG AA — accessibility (contrast, keyboard, screen readers, etc.)", value: "wcag-aa" },
        { name: "PCI DSS — payment card data (encryption, tokenization, access control)", value: "pci-basic" },
        { name: "HIPAA — health data (PHI protection, audit trails, encryption)", value: "hipaa-basic" },
        { name: "GDPR — data privacy (consent, deletion rights, data minimization)", value: "gdpr-basic" },
        { name: "SOC 2 — security + availability (MFA, logging, incident response)", value: "soc2-basic" },
      ],
    });

    const customRulesStr = await input({
      message: "Any custom guardrails? (comma-separated, or press enter to skip)",
    });
    const customRules = customRulesStr
      ? customRulesStr.split(",").map((r) => r.trim()).filter(Boolean)
      : [];

    guardrails = {
      security: { templates, custom: customRules },
      quality: [],
      process: [],
    };
  }

  // Tech context
  const stackStr = await input({ message: "Tech stack? (comma-separated, e.g. typescript, next.js, tailwind)" });
  const opinionsStr = await input({ message: "Any strong tech opinions? (comma-separated, or press enter to skip)" });

  const tech = (stackStr || opinionsStr) ? {
    stack: stackStr ? stackStr.split(",").map((s) => s.trim()).filter(Boolean) : [],
    opinions: opinionsStr ? opinionsStr.split(",").map((s) => s.trim()).filter(Boolean) : [],
  } : undefined;

  return {
    project,
    description,
    ...(audience ? { audience } : {}),
    values,
    ...(budget ? { budget } : {}),
    ...(guardrails ? { guardrails } : {}),
    ...(tech ? { tech } : {}),
  };
}

/**
 * Print the progress bar for a returning project.
 */
function printProgress(state: ReturnType<typeof readWorkflowState>): void {
  if (!state || state.featuresTotal === 0) return;

  const pct = Math.round((state.featuresComplete / state.featuresTotal) * 100);
  const spent = state.totalSpent.toFixed(2);
  const budget = state.totalBudget.toFixed(2);

  console.log(
    chalk.dim(
      `  ${state.featuresComplete}/${state.featuresTotal} features complete (${pct}%) — $${spent} of $${budget} spent`,
    ),
  );
}

async function runSettingsFlow(projectName: string): Promise<void> {
  let done = false;
  while (!done) {
    const setting = await select({
      message: "What would you like to update?",
      choices: [
        { name: "Re-capture project intent", value: "intent" },
        { name: "Update budget", value: "budget" },
        { name: "Reset build mode preference", value: "build-mode" },
        { name: "Back", value: "back" },
      ],
    });

    switch (setting) {
      case "intent": {
        const intentData = await captureIntentFlow(projectName);
        const { saveIntent } = await import("@fathom/intent");
        await saveIntent(process.cwd(), intentData);
        const { writeProjections } = await import("@fathom/projections");
        const files = await writeProjections(process.cwd(), intentData);
        console.log(chalk.green(`\n  ✓ Intent saved to .fathom/intent.yaml`));
        console.log(chalk.green(`  ✓ Generated ${files.length} projection files`));
        console.log();
        break;
      }
      case "budget": {
        const { loadIntent, saveIntent } = await import("@fathom/intent");
        let existing;
        try {
          existing = await loadIntent(process.cwd());
        } catch {
          console.log(chalk.yellow("  No intent found. Run intent capture first."));
          break;
        }
        const limitStr = await input({ message: "Monthly budget (USD):", default: String(existing.budget?.monthly_limit ?? 200) });
        const limit = parseFloat(limitStr) || 200;
        const thresholdStr = await input({ message: "Alert threshold (0-1):", default: String(existing.budget?.alert_threshold ?? 0.8) });
        const threshold = parseFloat(thresholdStr) || 0.8;
        existing.budget = {
          monthly_limit: limit,
          alert_threshold: threshold,
          prefer_batch: existing.budget?.prefer_batch ?? false,
          currency: existing.budget?.currency ?? "USD",
        };
        await saveIntent(process.cwd(), existing);
        console.log(chalk.green(`\n  ✓ Budget updated: $${limit}/month, alert at ${(threshold * 100).toFixed(0)}%\n`));
        break;
      }
      case "build-mode": {
        updateProjectConfig({ buildMode: undefined });
        console.log(chalk.green("\n  ✓ Build mode preference cleared. You'll be asked next time.\n"));
        break;
      }
      case "back":
        done = true;
        break;
    }
  }
}

export const startCommand = new Command("start")
  .alias("go")
  .description("Run the full workflow: intake → build → review")
  .option("--from <phase>", "Start from a specific phase (intake, build, review)")
  .option("--auto", "Skip confirmation prompts")
  .action(async (options: { from?: string; auto?: boolean }) => {
    try {
    const projectState = detectProjectState();
    const config = readProjectConfig();
    const workflowState = readWorkflowState();
    let projectName = config?.project ?? "unnamed";

    // ── Header ──
    console.log();

    if (projectState === "new") {
      console.log(chalk.bold("  Welcome to Fathom"));
      console.log();
      console.log(chalk.dim("  No project detected. Let's set one up."));
      console.log();

      // Intent-first setup
      const intentData = await captureIntentFlow(basename(process.cwd()));
      projectName = intentData.project;

      // Save intent
      const { saveIntent } = await import("@fathom/intent");
      await saveIntent(process.cwd(), intentData);

      // Scaffold .fathom/ (directories, registry, tracking)
      scaffoldProject(projectName, { quiet: true });
      updateProjectConfig({ project: projectName });

      // Generate projections
      const { writeProjections } = await import("@fathom/projections");
      const files = await writeProjections(process.cwd(), intentData);

      console.log(chalk.green(`\n  ✓ Project "${projectName}" initialized`));
      console.log(chalk.green(`  ✓ Intent saved to .fathom/intent.yaml`));
      console.log(chalk.green(`  ✓ Generated ${files.length} projection files`));
      console.log();
    } else if (projectState === "returning") {
      console.log(chalk.bold(`  Fathom — ${projectName}`));
      printProgress(workflowState);
    } else {
      console.log(chalk.bold(`  Fathom — ${projectName}`));
    }
    console.log();

    // Pricing staleness check (RES-01)
    try {
      const { checkPricingStaleness } = await import("../staleness.js");
      const staleness = checkPricingStaleness();
      if (staleness.stale) {
        warnAction(
          "Pricing data may be outdated",
          `Last updated ${staleness.daysSinceUpdate ?? "unknown"} days ago`,
          "Run: fathom research --auto-update",
        );
        console.log();
      }
    } catch {
      // Non-fatal — proceed without staleness check
    }

    // ── Determine starting phase ──
    let startPhase: WorkflowPhase;
    if (options.from) {
      const valid: WorkflowPhase[] = ["intake", "build", "review"];
      if (!valid.includes(options.from as WorkflowPhase)) {
        fatalError("Invalid phase", options.from + " is not a valid phase", "Use: intake, build, or review");
      }
      startPhase = options.from as WorkflowPhase;
    } else if (projectState === "returning") {
      // Show context menu for returning projects
      const action = await select({
        message: "What would you like to do?",
        choices: [
          {
            name: `Continue building${workflowState?.buildQueue[0] ? ` (next: ${workflowState.buildQueue[0]})` : ""}`,
            value: "build",
          },
          { name: "New intake — add more work", value: "intake" },
          { name: "Review — reconcile recent sessions", value: "review" },
          { name: "Update intent / project settings", value: "settings" },
          { name: "Status overview", value: "status" },
        ],
      });

      if (action === "status") {
        printStatus(workflowState);
        return;
      }
      if (action === "settings") {
        await runSettingsFlow(projectName);
        return;
      }
      startPhase = action as WorkflowPhase;
    } else {
      // New or initialized project — start from intake
      startPhase = "intake";
    }

    // ── Run phases sequentially ──
    const phaseIndex = PHASES.findIndex((p) => p.id === startPhase);
    for (let i = phaseIndex; i < PHASES.length; i++) {
      const phase = PHASES[i];

      console.log(chalk.bold(`  ── ${phase.label} ${"─".repeat(40 - phase.label.length)}`));
      console.log(chalk.dim(`  ${phase.description}`));
      console.log();

      // Checkpoint: confirm before each phase (unless --auto or first phase)
      if (i > phaseIndex && !options.auto) {
        const proceed = await confirm({
          message: `Continue to ${phase.label.toLowerCase()} phase?`,
          default: true,
        });
        if (!proceed) {
          console.log(chalk.dim("\n  Stopped. Resume with: fathom start --from " + phase.id));
          return;
        }
        console.log();
      }

      // Phase execution
      switch (phase.id) {
        case "intake": {
          const { result, specPath } = await runIntakeFlow(projectName);
          const newQueue = result.slices
            .sort((a, b) => {
              const priOrder = { P0: 0, P1: 1, P2: 2, P3: 3 };
              return (priOrder[a.priority as keyof typeof priOrder] ?? 9)
                - (priOrder[b.priority as keyof typeof priOrder] ?? 9);
            })
            .map((s) => s.sliceId);

          const existingState = readWorkflowState();

          if (existingState && existingState.featuresTotal > 0) {
            // Merge new features into existing workflow state
            const mergedQueue = [...new Set([...existingState.buildQueue, ...newQueue])];
            const newP1s = result.slices.filter((s) => s.priority === "P0" || s.priority === "P1");

            updateWorkflowState({
              ...existingState,
              phase: "build",
              specPath: specPath,
              featuresTotal: existingState.featuresTotal + result.slices.length,
              totalBudget: existingState.totalBudget + result.totalEstimatedCost,
              buildQueue: mergedQueue,
            });

            console.log(chalk.green(`\n  ✓ ${result.slices.length} new features added (${existingState.featuresTotal + result.slices.length} total)`));

            // Reprioritization prompt for new P1 items
            if (newP1s.length > 0 && !options.auto) {
              console.log(chalk.bold(`\n  ${newP1s.length} new high-priority item${newP1s.length === 1 ? "" : "s"}: ${newP1s.map((s) => s.name).join(", ")}`));
              const reprioritize = await confirm({
                message: "Move new P0/P1 items to front of build queue?",
                default: true,
              });
              if (reprioritize) {
                const p1Ids = new Set(newP1s.map((s) => s.sliceId));
                const reordered = [
                  ...newQueue.filter((id) => p1Ids.has(id)),
                  ...mergedQueue.filter((id) => !p1Ids.has(id)),
                ];
                updateWorkflowState({
                  ...readWorkflowState()!,
                  buildQueue: reordered,
                });
                console.log(chalk.green("  ✓ Build queue reprioritized"));
              }
            }
          } else {
            // Fresh workflow state
            updateWorkflowState({
              phase: "build",
              specPath,
              featuresTotal: result.slices.length,
              featuresComplete: 0,
              totalBudget: result.totalEstimatedCost,
              totalSpent: 0,
              buildQueue: newQueue,
            });
          }
          break;
        }
        case "build": {
          const currentState = readWorkflowState();
          if (!currentState || currentState.buildQueue.length === 0) {
            console.log(chalk.dim("  No features in build queue."));
            break;
          }

          // Generate build prompt
          const promptPath = generateBuildPrompt({
            projectName,
            workflowState: currentState,
          });
          console.log(chalk.green(`  ✓ Build prompt generated`));

          // Show what's next
          const nextFeatures = currentState.buildQueue.slice(0, 3);
          console.log(chalk.dim(`  Next up: ${nextFeatures.join(", ")}`));
          console.log();

          // Router recommendation (RTR-02)
          try {
            const router = createRouter();
            const nextFeature = currentState.buildQueue[0] ?? "general task";
            const routerTask: RouterTaskContext = {
              description: nextFeature,
              complexity: "M", // Default -- go doesn't have complexity info
            };
            const routerRec = router.recommend(routerTask);
            console.log(chalk.dim(`  Recommended model: ${routerRec.model} — ${routerRec.reason}`));
            if (routerRec.deprecationWarning) {
              console.log(chalk.yellow(`  ⚠ ${routerRec.deprecationWarning}`));
            }
            console.log();
          } catch {
            // Router failure is non-fatal — proceed without recommendation
          }

          // Resolve build mode: saved preference or ask
          const savedMode = readProjectConfig()?.buildMode;
          let buildMode: BuildMode;

          if (savedMode) {
            buildMode = savedMode;
            console.log(chalk.dim(`  Using saved build mode: ${buildMode}`));
          } else {
            const chosen = await select({
              message: "How would you like to build?",
              choices: [
                { name: "Launch Claude Code (recommended)", value: "launch" as const },
                { name: "Launch in worktree (isolated branch per feature)", value: "worktree" as const },
                { name: "I'll handle it — just give me the spec", value: "manual" as const },
              ],
            });
            buildMode = chosen;
            updateProjectConfig({ buildMode });
          }

          const skipReview = await executeBuildMode(buildMode, promptPath, currentState);
          if (skipReview) {
            // Manual mode — user builds externally, skip remaining phases
            return;
          }
          break;
        }
        case "review": {
          await runReviewPhase(projectName);
          break;
        }
      }

      console.log();
    }

    // ── Done ──
    console.log(chalk.bold("  ── DONE " + "─".repeat(38)));
    const finalState = readWorkflowState();
    if (finalState) {
      printProgress(finalState);
    }
    console.log();

    // Offer to continue if there are features remaining
    if (!options.auto && finalState && finalState.buildQueue.length > 0) {
      const cont = await confirm({
        message: "Continue building?",
        default: true,
      });
      if (cont) {
        console.log(chalk.dim("\n  Restarting from build phase...\n"));
        await startCommand.parseAsync(["node", "fathom", "start", "--from", "build"], { from: "node" });
      }
    }
    } catch (err: unknown) {
      // Handle Ctrl+C gracefully from @inquirer/prompts
      if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
        console.log(chalk.dim("\n  Exiting.\n"));
        return;
      }
      throw err;
    }
  });

/**
 * Run the review phase: track → reconcile → calibrate.
 * Each step runs as a subprocess using the fathom CLI.
 */
async function runReviewPhase(
  projectName: string,
): Promise<void> {
  const steps = [
    { label: "Tracking", cmd: ["track", "--project", projectName] },
    { label: "Reconciling", cmd: ["reconcile", "--project", projectName] },
    { label: "Calibrating", cmd: ["calibrate", "--apply", "--project", projectName] },
  ];

  for (const step of steps) {
    process.stdout.write(chalk.dim(`  ${step.label}... `));
    try {
      const { execFileSync } = await import("node:child_process");
      execFileSync(process.argv[0], [process.argv[1], ...step.cmd], {
        stdio: ["inherit", "pipe", "pipe"],
        cwd: process.cwd(),
      });
      console.log(chalk.green("✓"));
    } catch {
      console.log(chalk.yellow("⚠ (no data or error)"));
    }
  }

  // Update workflow state
  const state = readWorkflowState();
  if (state) {
    updateWorkflowState({ ...state, phase: "done" });
  }

  console.log();
}

/**
 * Execute the chosen build mode.
 * Returns true if remaining phases should be skipped (manual mode).
 */
async function executeBuildMode(
  mode: BuildMode,
  promptPath: string,
  state: NonNullable<ReturnType<typeof readWorkflowState>>,
): Promise<boolean> {
  switch (mode) {
    case "launch": {
      console.log(chalk.dim("\n  Launching Claude Code..."));
      console.log(chalk.dim(`  Context: ${state.featuresTotal} features, starting with ${state.buildQueue[0] ?? "queue"}`));
      console.log(chalk.dim("  " + "─".repeat(50)));

      await launchClaude(promptPath, undefined, state.buildQueue[0]);

      console.log(chalk.dim("  " + "─".repeat(50)));
      console.log(chalk.dim("  Session complete."));
      updateWorkflowState({ ...state, phase: "review", lastSessionAt: new Date().toISOString() });
      return false;
    }
    case "worktree": {
      const featureId = state.buildQueue[0];
      if (!featureId) {
        console.log(chalk.yellow("  No features in queue for worktree."));
        return false;
      }

      // SEC-01: Validate featureId to prevent command injection
      if (!/^[a-zA-Z0-9_-]+$/.test(featureId)) {
        console.log(chalk.red(`  Invalid feature ID: "${featureId}" — only letters, digits, hyphens, underscores allowed`));
        return false;
      }

      const worktreePath = resolve(process.cwd(), ".worktrees", featureId);
      const branchName = `feat/${featureId}`;

      console.log(chalk.dim(`\n  Creating worktree: ${branchName}`));
      try {
        // SEC-01: Use spawnSync with array args instead of execSync with string interpolation
        const { spawnSync } = await import("node:child_process");
        const result = spawnSync("git", ["worktree", "add", worktreePath, "-b", branchName], {
          stdio: "pipe",
          cwd: process.cwd(),
        });
        if (result.status !== 0) {
          throw new Error(result.stderr?.toString() || "git worktree failed");
        }
        console.log(chalk.green(`  ✓ Worktree created at ${worktreePath}`));
      } catch {
        console.log(chalk.yellow(`  ⚠ Worktree creation failed — launching in current directory`));
      }

      const launchDir = existsSync(worktreePath) ? worktreePath : process.cwd();
      console.log(chalk.dim(`  Launching Claude Code in ${launchDir}...`));
      console.log(chalk.dim("  " + "─".repeat(50)));

      await launchClaude(promptPath, launchDir, state.buildQueue[0]);

      console.log(chalk.dim("  " + "─".repeat(50)));
      console.log(chalk.dim("  Session complete."));
      updateWorkflowState({ ...state, phase: "review", lastSessionAt: new Date().toISOString() });
      return false;
    }
    case "manual": {
      console.log();
      console.log(chalk.bold("  Build manually:"));
      console.log(`  Spec:         ${state.specPath ?? "(none)"}`);
      console.log(`  Build prompt: ${promptPath}`);
      console.log(`  Next feature: ${state.buildQueue[0] ?? "(none)"}`);
      console.log();
      console.log(chalk.dim("  When done, run: fathom start --from review"));
      updateWorkflowState({ ...state, phase: "review", lastSessionAt: new Date().toISOString() });
      return true;
    }
  }
  return false;
}

/**
 * Launch Claude Code with the build prompt.
 * Passes build context via --append-system-prompt and an initial instruction
 * as the positional prompt so Claude starts working immediately.
 */
function launchClaude(promptPath: string, cwd?: string, firstFeature?: string): Promise<void> {
  return new Promise((resolve) => {
    const promptContent = readFileSync(promptPath, "utf-8");
    const initialPrompt = firstFeature
      ? `Read the build context in your system prompt. Start working on feature \`${firstFeature}\` — read the spec reference, plan the implementation, and begin.`
      : "Read the build context in your system prompt and start working on the first feature in the priority queue.";
    const child = spawn("claude", ["--append-system-prompt", promptContent, initialPrompt], {
      stdio: "inherit",
      cwd: cwd ?? process.cwd(),
    });

    // Ignore SIGINT while Claude is running — let Claude handle it.
    // Without this, Ctrl+C in Claude kills the parent fathom process too.
    const sigintHandler = () => {};
    process.on("SIGINT", sigintHandler);

    child.on("error", (err) => {
      process.removeListener("SIGINT", sigintHandler);
      if ((err as NodeJS.ErrnoException).code === "ENOENT") {
        fatalError("Claude Code not found", "Could not launch Claude Code", "Install from https://claude.ai/code");
      } else {
        console.error(chalk.red(`  Failed to launch Claude Code: ${err.message}`));
      }
      resolve();
    });

    child.on("close", () => {
      process.removeListener("SIGINT", sigintHandler);
      resolve();
    });
  });
}

function printStatus(state: ReturnType<typeof readWorkflowState>): void {
  if (!state) {
    console.log(chalk.dim("  No workflow state found."));
    return;
  }

  console.log(chalk.bold("\n  Status Overview"));
  console.log(chalk.dim("  " + "─".repeat(40)));
  console.log(`  Phase:            ${state.phase}`);
  console.log(`  Features:         ${state.featuresComplete}/${state.featuresTotal}`);
  console.log(`  Budget:           $${state.totalBudget.toFixed(2)}`);
  console.log(`  Spent:            $${state.totalSpent.toFixed(2)}`);
  if (state.buildQueue.length > 0) {
    console.log(`  Next in queue:    ${state.buildQueue.slice(0, 3).join(", ")}`);
  }
  if (state.lastSessionAt) {
    console.log(`  Last session:     ${state.lastSessionAt}`);
  }
  console.log();
}
